import {dateToStr} from './delphiFormat.js'

/**
 * FrameTracker's Excel export, for reports that declare one.
 *
 * FrameTracker's Excel button writes its own column list (the SMExport
 * component's Columns, often different from the printout) under its own title
 * rows, and Monthly Invoice writes a second file. `legacyLayout.export` carries
 * that: {columns: [{key, label}], titleRows: [templates], extraFiles: [{name,
 * source, columns}]}. This module turns rows into exactly that table and writes
 * it as CSV or as a real .xlsx (a minimal SpreadsheetML package zipped with the
 * JSZip build DataTables already ships), so the columns, titles and order are
 * ours to control rather than DataTables'.
 *
 * Values: numeric columns (type money, price, int, days, number, float,
 * percent) go out as numbers; date columns as DateToStr (m/d/yyyy); everything
 * else as text. The type comes from the export column itself or, failing that,
 * from the report column of the same key.
 *
 * buildExportTable and toCsv are pure: checked by scripts/report-checks.mjs.
 */

const NUMERIC_TYPES = ['money', 'price', 'int', 'days', 'number', 'float', 'percent']

// A formatted number ('-1,234.50', '-$5.00', '+3', '12 %'): left alone, so a
// negative figure is not mistaken for a formula.
const NUMERIC_TEXT = /^[-+]?\(?\$?[\d,]*\.?\d+\)?\s?%?$/

/**
 * CSV / formula injection guard (OWASP): a TEXT cell beginning with =, +, -,
 * @, tab or carriage return could run as a formula when the file is opened in
 * a spreadsheet, so it gets a leading single quote. Numbers, and text that is
 * just a formatted number, are left untouched so totals stay numeric. An
 * approved boundary change: FrameTracker's export wrote the text as it was.
 */
export function neutralize(value) {
    if (typeof value !== 'string' || value === '') return value
    if (!/^[=+\-@\t\r]/.test(value)) return value
    if (NUMERIC_TEXT.test(value.trim())) return value
    return "'" + value
}

function cellValue(value, column) {
    if (value === null || value === undefined) return ''
    const type = column && column.type
    if (type === 'date' || type === 'datetime') return dateToStr(value)
    if (NUMERIC_TYPES.includes(type)) {
        const num = Number(value)
        return value !== '' && Number.isFinite(num) ? num : String(value)
    }
    return typeof value === 'number' ? value : String(value)
}

/**
 * {titleRows: [string], header: [string], body: [[value]]}.
 *
 * columns:   the export's [{key, label}] in order.
 * types:     {key: column} from the report's own columns, for the value types.
 * titleRows: already rendered title lines.
 */
export function buildExportTable({columns, rows, titleRows = [], types = {}}) {
    const cols = (columns || []).map(c => (typeof c === 'string' ? {key: c, label: c} : c))
    return {
        titleRows: titleRows.map(neutralize),
        header: cols.map(c => neutralize(c.label !== undefined && c.label !== null ? String(c.label) : c.key)),
        body: (rows || []).map(row => cols.map(c => neutralize(cellValue(row[c.key], {...(types[c.key] || {}), ...c})))),
    }
}

function csvField(value) {
    const text = value === null || value === undefined ? '' : String(value)
    return /[",\r\n]/.test(text) ? '"' + text.replace(/"/g, '""') + '"' : text
}

/** RFC 4180 CSV, CRLF line ends; each title row is one field on its own line. */
export function toCsv(table) {
    const lines = []
    table.titleRows.forEach(t => lines.push(csvField(t)))
    lines.push(table.header.map(csvField).join(','))
    table.body.forEach(r => lines.push(r.map(csvField).join(',')))
    return lines.join('\r\n') + '\r\n'
}

function xmlEscape(text) {
    return String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        // Characters XML 1.0 forbids would make Excel refuse the file.
        // eslint-disable-next-line no-control-regex
        .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '')
}

function columnName(index) {
    let name = ''
    let n = index + 1
    while (n > 0) {
        const rem = (n - 1) % 26
        name = String.fromCharCode(65 + rem) + name
        n = Math.floor((n - 1) / 26)
    }
    return name
}

/** The worksheet XML: inline strings, so no shared-strings table is needed. */
export function sheetXml(table) {
    const rows = []
    let r = 0
    const cell = (value, c, style) => {
        const ref = columnName(c) + (r + 1)
        const s = style ? ' s="' + style + '"' : ''
        if (typeof value === 'number') return '<c r="' + ref + '"' + s + '><v>' + value + '</v></c>'
        if (value === '') return ''
        return '<c r="' + ref + '" t="inlineStr"' + s + '><is><t xml:space="preserve">' + xmlEscape(value) + '</t></is></c>'
    }
    const row = (values, style) => {
        rows.push('<row r="' + (r + 1) + '">' + values.map((v, c) => cell(v, c, style)).join('') + '</row>')
        r++
    }

    table.titleRows.forEach(t => row([t], 1))
    row(table.header, 1)
    table.body.forEach(values => row(values, 0))

    return '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">' +
        '<sheetData>' + rows.join('') + '</sheetData></worksheet>'
}

/** A .xlsx Blob for one table. JSZip is passed in so this module stays pure. */
export function toXlsx(table, JSZip, sheetName = 'Sheet1') {
    const zip = new JSZip()
    zip.file('[Content_Types].xml',
        '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">' +
        '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>' +
        '<Default Extension="xml" ContentType="application/xml"/>' +
        '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>' +
        '<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>' +
        '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>' +
        '</Types>')
    zip.file('_rels/.rels',
        '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
        '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>' +
        '</Relationships>')
    zip.file('xl/workbook.xml',
        '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        '<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" ' +
        'xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">' +
        '<sheets><sheet name="' + xmlEscape(sheetName.slice(0, 31)) + '" sheetId="1" r:id="rId1"/></sheets></workbook>')
    zip.file('xl/_rels/workbook.xml.rels',
        '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
        '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>' +
        '<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>' +
        '</Relationships>')
    // Style 1 is bold, for the title and header rows.
    zip.file('xl/styles.xml',
        '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        '<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">' +
        '<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts>' +
        '<fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills>' +
        '<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>' +
        '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>' +
        '<cellXfs count="2"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>' +
        '<xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/></cellXfs>' +
        '</styleSheet>')
    zip.file('xl/worksheets/sheet1.xml', sheetXml(table))
    return zip.generateAsync({
        type: 'blob',
        mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    })
}

/** Hands a Blob to the browser as a download. */
export function download(blob, filename) {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    setTimeout(() => URL.revokeObjectURL(url), 1000)
}
